<template>
  <div class="taglist-sdaa-container">
    <!-- 헤더 -->
    <div class="header">
      <div class="header-left">
        <button @click="goBack" class="back-button">
          <span class="back-arrow">←</span>
        </button>
        <div class="header-text">
          <h2>{{ selectedWard ? selectedWard + '병동 ' : '' }}보유중인 {{ selectedDevice }}</h2>
          <p class="hospital-name">{{ tenant.hospitalName || tenant.displayName }}</p>
        </div>
      </div>
    </div>

    <!-- 검색 및 필터 -->
    <div class="search-section">
      <div class="search-group">
        <input 
          type="text" 
          v-model="searchText" 
          class="search-input"
          placeholder="검색어를 입력하세요..."
          @input="handleSearch"
        >
        <div class="filter-buttons">
          <button 
            v-for="ward in wards" 
            :key="ward"
            @click="filterByWard(ward)"
            :class="['filter-btn', { active: selectedWard === ward }]"
          >
            {{ ward }}병동
          </button>
        </div>
      </div>
    </div>

    <!-- 로딩 상태 -->
    <div v-if="loading" class="loading">데이터 불러오는 중...</div>

    <!-- 장비 카드 그리드 -->
    <div v-else class="device-cards">
      <div 
        v-for="device in filteredDevices" 
        :key="device.tag_mac" 
        class="device-card"
        :class="{ 'selected': selectedDevice === device.devicename }"
      >
        <!-- 장비 이미지 (사각형, 위쪽) -->
        <div class="device-image-container">
          <img
            :src="getDeviceImage(device.devicetype)"
            :alt="device.devicename"
            class="device-image"
            @error="onImageError"
          />
          
          <!-- 상태 표시 (이미지 위에 오버레이) -->
          <div class="status-indicator" :class="getStatusClass(device)">
            <span class="status-dot"></span>
            <span class="status-text">{{ getStatusText(device) }}</span>
          </div>
          
          <!-- 장비 식별자 (이미지 안에 오버레이) -->
          <div class="device-identifier-overlay">
            {{ device.devicename }}
          </div>
        </div>

        <!-- 위치 정보 버튼만 남기기 -->
        <div class="card-footer">
          <button @click="showLocationInfo(device)" class="location-btn">
            위치정보
          </button>
        </div>
      </div>
    </div>


  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import axios from "axios";
import tenantState from "@/tenant";

const router = useRouter();
const route = useRoute();
const tenant = tenantState.tenant;

// 상태 변수들
const loading = ref(true);
const devices = ref([]);
const searchText = ref("");
const wards = tenant.wards.map((w) => w.code);   // 병동 목록은 테넌트 프리셋에서
const selectedWard = ref(wards[0] || "");
const selectedDevice = ref("");


// (wards 는 위에서 테넌트 프리셋으로 정의)

// 라우트 쿼리에서 장비 타입 추출
onMounted(() => {
  const query = route.query;
  if (query.searchText) {
    const parts = query.searchText.split('_');
    if (parts.length >= 2 && wards.includes(parts[0])) {
      selectedWard.value = parts[0];
      selectedDevice.value = parts.slice(1).join('_');
    } else {
      selectedDevice.value = query.searchText;
    }
  }
  fetchDevices();
});

// 장비 데이터 가져오기
const fetchDevices = async () => {
  try {
    loading.value = true;
    const company = localStorage.getItem("company") || "SDAA";
    const response = await axios.get("/api/tagList", {
      params: {
        company,
        devicename: selectedWard.value ? `${selectedWard.value}_${selectedDevice.value}` : selectedDevice.value,
        limit: 100
      }
    });
    
    devices.value = response.data.data || [];
  } catch (error) {
    console.error("장비 데이터 가져오기 실패:", error);
    devices.value = [];
  } finally {
    loading.value = false;
  }
};

// 필터링된 장비 목록
const filteredDevices = computed(() => {
  if (!searchText.value) return devices.value;
  
  return devices.value.filter(device => 
    device.devicename?.toLowerCase().includes(searchText.value.toLowerCase()) ||
    device.taglocation?.toLowerCase().includes(searchText.value.toLowerCase())
  );
});

// 검색 처리
const handleSearch = () => {
  // 실시간 검색 (필요시 디바운싱 추가)
};

// 병동별 필터링
const filterByWard = (ward) => {
  selectedWard.value = ward;
  fetchDevices();
};

// 뒤로가기
const goBack = () => {
  router.push("/dashboardSDAA");
};

// 장비 이미지 가져오기
const getDeviceImage = (deviceType) => {
  const imageMap = {
    "DVT": require("@/assets/dvt.png"),
    "PulseOxi": require("@/assets/pulse-oxi.png"),
    "SCD_P": require("@/assets/scd.png"),
    "Nebulizer": require("@/assets/nebulizer.png"),
    "PatientM": require("@/assets/patient-monitor.png"),
    "InfusionP": require("@/assets/infusion-pump.png"),
    "VarioS": require("@/assets/varios.png"),
    "FeedingP": require("@/assets/feeding-pump.png"),
    "HPO": require("@/assets/hpo.png")
  };
  
  return imageMap[deviceType] || require("@/assets/default-device.png");
};

// 상태 클래스 및 텍스트
const getStatusClass = (device) => {
  if (device.active === 0) return "status-active";
  return "status-inactive";
};

const getStatusText = (device) => {
  if (device.active === 0) {
    return device.taglocation || "사용중";
  }
  return "비활성";
};

// 위치 정보 표시 - AssetMap으로 이동
const showLocationInfo = () => {
  // 간단하게 AssetMap으로 이동
  router.push('/assetMap');
};



// 이미지 에러 처리
const onImageError = (event) => {
  event.target.src = require("@/assets/default-device.png");
};
</script>

<style scoped>
.taglist-sdaa-container {
  padding: 24px;
  background-color: #f9fafb;
  min-height: 100vh;
}

/* 헤더 */
.header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.back-button {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s;
}

.back-button:hover {
  background-color: #f3f4f6;
  color: #374151;
}

.back-arrow {
  font-weight: bold;
}

.header-text h2 {
  margin: 0 0 8px 0;
  font-size: 24px;
  font-weight: 600;
  color: #111827;
}

.hospital-name {
  margin: 0;
  font-size: 16px;
  color: #6b7280;
}

/* 검색 섹션 */
.search-section {
  margin-bottom: 24px;
}

.search-group {
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
}

.search-input {
  flex: 1;
  min-width: 300px;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  background: white;
}

.search-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.filter-buttons {
  display: flex;
  gap: 8px;
}

.filter-btn {
  padding: 8px 16px;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.filter-btn:hover {
  background-color: #f9fafb;
}

.filter-btn.active {
  background-color: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

/* 로딩 */
.loading {
  text-align: center;
  color: #6b7280;
  font-size: 16px;
  margin-top: 40px;
}

/* 장비 카드 그리드 */
.device-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.device-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
}

.device-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.device-card.selected {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* 장비 이미지 (사각형, 위쪽) */
.device-image-container {
  position: relative;
  height: 200px;
  background: #f3f4f6;
  flex: 1;
}

.device-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.status-indicator {
  position: absolute;
  bottom: 8px;
  left: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  background:  rgba(0, 0, 0, 0.7);
  color: white;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 20px;
  font-weight: 500;
  backdrop-filter: blur(4px);
  z-index: 3;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.status-active .status-dot {
  background-color: #10b981;
}

.status-inactive .status-dot {
  background-color: #ef4444;
}

/* 장비 식별자 오버레이 (이미지 안에) */
.device-identifier-overlay {
  position: absolute;
  bottom: 60px;
  left: 16px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 24px;
  font-weight: 600;
  backdrop-filter: blur(4px);
  z-index: 2;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
}

/* 카드 푸터 (버튼만) */
.card-footer {
  padding: 16px;
  background: #f8f9fa;
  border-top: 1px solid #e5e7eb;
}

/* 위치 정보 버튼 */
.location-btn {
  width: 100%;
  padding: 12px;
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.location-btn:hover {
  background-color: #2563eb;
}



/* 반응형 */
@media (max-width: 768px) {
  .taglist-sdaa-container {
    padding: 16px;
  }
  
  .device-cards {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  }
  
  .search-group {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-input {
    min-width: auto;
  }
  
  .device-image-container {
    height: 180px;
  }
}

@media (max-width: 480px) {
  .device-cards {
    grid-template-columns: 1fr;
  }
  
  .device-image-container {
    height: 160px;
  }
  
  .card-footer {
    padding: 12px;
  }
}
</style> 